import * as React from 'react';
import { useItem } from '../../../hooks/item/ItemProvider';
import {
  Divider,
  Link,
  makeStyles,
  MessageBar,
  MessageBarActions,
  MessageBarBody,
  MessageBarGroup,
  Skeleton,
  SkeletonItem,
  Text,
  tokens,
} from '@fluentui/react-components';
import { useClient } from '../../../hooks/client';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigation } from '../../../hooks/routing';
import CardSelector, { CardSelectorProps } from '../../../components/CardSelector';
import { StakeholderInformation } from '../../../hooks/client/types/CreateEmailObjectRequest';
import { StakeholderCard } from '../../../components/StakeholderCard';
import { AddFilled, DismissRegular } from '@fluentui/react-icons';
import { AppRouteConfig } from '../../../hooks/routing/AppSpecificRouting';
import { getNameParts } from '../../../helpers/getNameParts';
import { Button } from '../../../components/Button';

const useStyles = makeStyles({
  container: {
    paddingInline: tokens.spacingHorizontalXXL,
    paddingBlock: tokens.spacingVerticalXXL,
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXL,
  },
  stakeholderList: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalL,
  },
  heading: {
    margin: 0,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
    display: 'block',
    fontSize: tokens.fontSizeBase400,
    marginBottom: tokens.spacingVerticalXS,
  },
  subheading: {
    color: tokens.colorNeutralForeground3,
  },
  headerGroup: {
    marginBottom: tokens.spacingVerticalXXL,
  },
  stakeholderSkeleton: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalL,
  },
  bannerContainer: {
    position: 'relative',
    zIndex: tokens.zIndexFloating,
  },
  bannerText: {
    fontSize: tokens.fontSizeBase200,
  },
});

const StakeholdersTab = () => {
  const { cmStakeholders, newStakeholders, isLoadingStakeholders, emailObject, setFormValue } = useItem();
  const { configuration } = useClient();
  const { t } = useTranslation();
  const styles = useStyles();
  const { setCurrentParams, params } = useNavigation<'main'>();

  const handleNewStakeholdersChanged = (stakeholders: Set<string | number>) => {
    const newStakeholderMap = new Map();

    for (const stakeholder of newStakeholders) {
      newStakeholderMap.set(stakeholder.email, stakeholders.has(stakeholder.email));
    }

    setFormValue('selectedNewStakeholders', newStakeholderMap);
  };

  const handleExistingStakeholdersChanged = (stakeholders: Set<string | number>) => {
    const newStakeholderMap = new Map();

    for (const stakeholder of cmStakeholders) {
      newStakeholderMap.set(stakeholder.id, stakeholders.has(stakeholder.id));
    }

    setFormValue('selectedExistingStakeholders', newStakeholderMap);
  };

  return (
    <div>
      <div className={styles.bannerContainer}>
        <MessageBarGroup>
          {params.createdStakeholderInformation ? (
            <MessageBar
              intent="success"
              shape="square"
            >
              <MessageBarBody>
                <Trans
                  i18nKey={'MAIN_SCREEN.CREATE_STAKEHOLDER_SUCCESS'}
                  values={{
                    stakeholderName: configuration.entityDefinitions.stakeholder.name,
                  }}
                  components={[
                    <Text
                      key={0}
                      className={styles.bannerText}
                    />,
                    <Link
                      className={styles.bannerText}
                      as="a"
                      target="_blank"
                      rel="noreferrer"
                      href={params.createdStakeholderInformation.cmUrl}
                      key={1}
                    />,
                  ]}
                />
              </MessageBarBody>
              <MessageBarActions
                containerAction={
                  <Button
                    onClick={() => setCurrentParams({ ...params, createdStakeholderInformation: undefined })}
                    aria-label="dismiss"
                    appearance="transparent"
                    icon={<DismissRegular />}
                  />
                }
              />
            </MessageBar>
          ) : null}
        </MessageBarGroup>
      </div>
      <div className={styles.container}>
        <div>
          <div className={styles.headerGroup}>
            <Text
              className={styles.heading}
              as="h2"
            >
              {t('STAKEHOLDERS_TAB.MATCHED', { stakeholderPluralName: configuration.entityDefinitions.stakeholder.pluralName })}
            </Text>
            <Text className={styles.subheading}>
              {t('STAKEHOLDERS_TAB.MATCHED_DESCRIPTION', { stakeholderPluralName: configuration.entityDefinitions.stakeholder.pluralName })}
            </Text>
          </div>
          <StakeholderSelector
            selectedStakeholders={emailObject.selectedExistingStakeholders}
            items={cmStakeholders}
            onSelectionChange={handleExistingStakeholdersChanged}
            emptyListPlaceholder={t('STAKEHOLDERS_TAB.MATCHED_SELECTOR.EMPTY', {
              stakeholderPluralName: configuration.entityDefinitions.stakeholder.pluralName,
            })}
            isLoading={isLoadingStakeholders}
          />
        </div>
        <Divider inset />
        <div>
          <div className={styles.headerGroup}>
            <Text
              className={styles.heading}
              as="h2"
            >
              {t('STAKEHOLDERS_TAB.UNKNOWN')}
            </Text>
            <Text className={styles.subheading}>
              {t('STAKEHOLDERS_TAB.UNKNOWN_DESCRIPTION', { stakeholderName: configuration.entityDefinitions.stakeholder.name })}
            </Text>
          </div>
          <StakeholderSelector
            selectedStakeholders={emailObject.selectedNewStakeholders}
            items={newStakeholders}
            onSelectionChange={handleNewStakeholdersChanged}
            emptyListPlaceholder={t('STAKEHOLDERS_TAB.UNKNOWN_SELECTOR.EMPTY')}
            isLoading={isLoadingStakeholders}
          />
        </div>
      </div>
    </div>
  );
};

export const StakeholderSelector = ({
  isLoading,
  selectedStakeholders,
  ...props
}: Omit<CardSelectorProps<StakeholderInformation, number | string>, 'selectedItems' | 'keySelector' | 'render'> & {
  isLoading: boolean;
  selectedStakeholders: Map<number | string, boolean>;
}) => {
  const styles = useStyles();
  const { navigate } = useNavigation<'main'>();
  const { emailObject } = useItem();
  const handleCreateStakeholder = (stakeholder: StakeholderInformation) => {
    const names = getNameParts(stakeholder.name);

    const defaultInformation: AppRouteConfig['stakeholders'] = {
      firstName: names.firstName ?? '',
      email: stakeholder.email,
      lastName: names.lastName ?? '',
      projectIds: emailObject.selectedProjects,
    };

    navigate('stakeholders', defaultInformation);
  };

  return isLoading ? (
    <Skeleton>
      <div className={styles.stakeholderSkeleton}>
        <SkeletonItem size={64} />
        <SkeletonItem size={64} />
      </div>
    </Skeleton>
  ) : (
    <CardSelector<StakeholderInformation, number | string>
      selectedItems={new Set([...selectedStakeholders].filter((x) => x[1]).map((x) => x[0]))}
      keySelector={(item) => item.id ?? item.email}
      render={(stakeholder) => (
        <StakeholderCard
          stakeholder={stakeholder}
          actions={
            !stakeholder.id && (
              <Button
                appearance="transparent"
                icon={
                  <AddFilled
                    onClick={() => handleCreateStakeholder(stakeholder)}
                    color={tokens.colorNeutralForeground3}
                  />
                }
              />
            )
          }
        />
      )}
      {...props}
    />
  );
};

export default StakeholdersTab;
